@if (showComposer()) {
  <div class="lead-timeline__composer">
    <app-admin-textarea
      name="newNote"
      [(value)]="newNoteText"
      [disabled]="noteSaving()"
      [rows]="3"
      [fullWidth]="true"
      placeholder="Write an observation about this lead…"
    />
    @if (noteError()) {
      <p class="lead-timeline__error" role="alert">{{ noteError() }}</p>
    }
    <div class="lead-timeline__composer-actions">
      <app-admin-button
        variant="primary"
        type="button"
        [disabled]="noteSaving() || !newNoteText.trim()"
        (click)="submitNote()"
      >
        <app-admin-icon name="plus" [size]="16" />
        {{ noteSaving() ? 'Saving…' : 'Add note' }}
      </app-admin-button>
    </div>
  </div>
}

@if (showActivity()) {
  @if (loading()) {
    <p class="cg-form-help">Loading activity…</p>
  } @else if (items().length === 0) {
    <app-admin-empty-state
      icon="clock"
      [title]="includeArchivedNotes() ? 'No deleted notes' : 'No activity yet'"
      [description]="
        includeArchivedNotes()
          ? 'Deleted notes will appear here when you archive a note.'
          : 'Notes and workflow events will appear here.'
      "
    />
  } @else {
    <ul class="lead-timeline__track">
      @for (item of items(); track item.id; let last = $last) {
        <li
          class="lead-timeline__event"
          [class.lead-timeline__event--workflow]="item.type === 'WORKFLOW_TRANSITION'"
          [class.lead-timeline__event--note]="isNoteItem(item)"
          [class.lead-timeline__event--note-archived]="isArchivedNote(item)"
          [class.lead-timeline__event--email]="item.type === 'EMAIL_SENT'"
          [class.lead-timeline__event--created]="item.type === 'LEAD_CREATED'"
          [class.lead-timeline__event--updated]="item.type === 'LEAD_UPDATED' || item.type === 'APPLICANT_UPDATED'"
          [class.lead-timeline__event--ownership]="isOwnershipItem(item)"
          [class.lead-timeline__event--deleted]="item.type === 'NOTE_DELETED'"
          [class.lead-timeline__event--editing]="editingNoteId() === item.payload.referenceId"
          [class.lead-timeline__event--last]="last"
        >
          <div class="lead-timeline__rail" aria-hidden="true">
            <span class="lead-timeline__dot"></span>
            @if (!last) {
              <span class="lead-timeline__line"></span>
            }
          </div>

          <div class="lead-timeline__event-body">
            <div class="lead-timeline__event-head">
              <strong class="lead-timeline__event-title">{{ eventTitle(item) }}</strong>
              <time class="lead-timeline__event-date">
                {{ item.createdAt | date: 'MMM d, y' }} • {{ item.createdAt | date: 'shortTime' }}
              </time>
            </div>

            @if (item.type === 'WORKFLOW_TRANSITION') {
              @if (workflowStatusChangeText(item); as statusChange) {
                <p class="lead-timeline__description">{{ statusChange }}</p>
              }
              @if (workflowComment(item); as comment) {
                <p class="lead-timeline__description">{{ comment }}</p>
              }
              @if (workflowSubStatusClearedText(item); as cleared) {
                <p class="lead-timeline__description lead-timeline__description--muted">{{ cleared }}</p>
              }
            } @else if (item.type === 'SUB_STATUS_CHANGED') {
              @if (subStatusChangeText(item); as description) {
                <p class="lead-timeline__description">{{ description }}</p>
              }
            } @else if (item.type === 'NOTE_ADDED' || item.type === 'NOTE_UPDATED') {
              @if (editingNoteId() === item.payload.referenceId) {
                <div class="lead-timeline__note-wrap lead-timeline__note-wrap--editing">
                  <app-admin-textarea
                    name="editNote"
                    [(value)]="editNoteText"
                    [rows]="2"
                    [fullWidth]="true"
                  />
                  <div class="lead-timeline__hover-menu" role="toolbar" aria-label="Note actions">
                    <button
                      type="button"
                      class="lead-timeline__icon-btn lead-timeline__icon-btn--confirm"
                      aria-label="Save note"
                      [disabled]="noteSaving()"
                      (click)="saveEditNote(item)"
                    >
                      <app-admin-icon name="check" [size]="15" />
                    </button>
                    <button
                      type="button"
                      class="lead-timeline__icon-btn"
                      aria-label="Cancel edit"
                      [disabled]="noteSaving()"
                      (click)="cancelEditNote()"
                    >
                      <app-admin-icon name="x" [size]="15" />
                    </button>
                  </div>
                </div>
              } @else {
                <div class="lead-timeline__note-wrap" [class.lead-timeline__note-wrap--archived]="isArchivedNote(item)">
                  <blockquote class="lead-timeline__note-box">{{ noteText(item) }}</blockquote>
                  @if (canRestoreNote(item)) {
                    <div class="lead-timeline__restore-actions">
                      <button
                        type="button"
                        class="lead-timeline__restore-btn"
                        [disabled]="noteSaving()"
                        (click)="restoreNote(item)"
                      >
                        <app-admin-icon name="rotate-ccw" [size]="14" />
                        Restore
                      </button>
                    </div>
                  } @else if (canModifyNote(item)) {
                    <div class="lead-timeline__hover-menu" role="toolbar" aria-label="Note actions">
                      <button
                        type="button"
                        class="lead-timeline__icon-btn"
                        aria-label="Edit note"
                        [disabled]="noteSaving()"
                        (click)="startEditNote(item)"
                      >
                        <app-admin-icon name="pencil" [size]="15" />
                      </button>
                      <button
                        type="button"
                        class="lead-timeline__icon-btn lead-timeline__icon-btn--danger"
                        aria-label="Delete note"
                        [disabled]="noteSaving()"
                        (click)="deleteNote(item)"
                      >
                        <app-admin-icon name="trash-2" [size]="15" />
                      </button>
                    </div>
                  }
                </div>
              }
            } @else if (item.type === 'EMAIL_SENT') {
              <div class="lead-timeline__email">
                @if (emailSubject(item); as subject) {
                  <p class="lead-timeline__email-subject">{{ subject }}</p>
                }
                @if (emailRecipientsText(item, 'to'); as toText) {
                  <p class="lead-timeline__email-meta"><span>To</span> {{ toText }}</p>
                }
                @if (emailRecipientsText(item, 'cc'); as ccText) {
                  <p class="lead-timeline__email-meta"><span>Cc</span> {{ ccText }}</p>
                }
                @if (emailRecipientsText(item, 'bcc'); as bccText) {
                  <p class="lead-timeline__email-meta"><span>Bcc</span> {{ bccText }}</p>
                }
                @if (emailPreview(item); as preview) {
                  <p class="lead-timeline__description">{{ preview }}</p>
                }
              </div>
            } @else if (item.type === 'NOTE_DELETED') {
              <p class="lead-timeline__description lead-timeline__description--muted">Note removed</p>
            } @else if (item.type === 'LEAD_UPDATED' || item.type === 'APPLICANT_UPDATED') {
              <ul class="lead-timeline__changes">
                @for (change of leadChanges(item); track change.field) {
                  <li>
                    <span class="lead-timeline__change-field">{{ fieldLabel(change.field) }}</span>:
                    {{ formatValue(change.oldValue) }} → {{ formatValue(change.newValue, true) }}
                  </li>
                }
              </ul>
            } @else if (isOwnershipItem(item)) {
              @if (ownershipChangeText(item); as ownershipText) {
                <p class="lead-timeline__description">{{ ownershipText }}</p>
              }
            } @else if (item.payload.description) {
              <p class="lead-timeline__description">{{ item.payload.description }}</p>
            }

            <p class="lead-timeline__attribution">{{ eventAttribution(item) }}</p>
          </div>
        </li>
      }
    </ul>

    @if (hasMore()) {
      <div class="lead-timeline__footer">
        <app-admin-button variant="secondary" type="button" [disabled]="loadingMore()" (click)="loadMore()">
          {{ loadingMore() ? 'Loading…' : 'Load more' }}
        </app-admin-button>
      </div>
    }
  }
}
